// Angular
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
// RxJS
import { Observable, of } from 'rxjs';
// CRUD
import { HttpUtilsService, QueryParamsModel, QueryResultsModel } from '../../_base/crud';
// Models
import { ContactModel } from '../_models/contact.model';

// Ngrx
import { select, Store } from '@ngrx/store';
import { AppState } from '../../reducers';
import { currentAuthToken, currentUser } from '../../auth';
import { environment } from '../../../../environments/environment';
import { cloneDeep, uniqWith, isEqual } from 'lodash-es';

@Injectable()
export class ContactsService {
  private authToken;
  private companyId;

  constructor(
    private http: HttpClient,
    private httpUtils: HttpUtilsService,
    private store: Store<AppState>,
  ) {
    this.store.pipe(select(currentAuthToken)).subscribe((res) => (this.authToken = res));
    this.store.pipe(select(currentUser)).subscribe((res) => (this.companyId = res.companyID));
  }

  private static getContactsListQueryString(queryParams: QueryParamsModel): string {
    const { pageNumber, pageSize, sortField, sortOrder, filter } = queryParams;
    let fromString = ``;

    if (!filter.isActive && sortField !== 'isActive') fromString = `sort=isActive,desc&`;
    fromString += `page=${pageNumber}&size=${pageSize}`;

    fromString += filter.isInbound
      ? `&caller_id=${filter.callerId}`
      : `${
          sortField.localeCompare('name') === 0
            ? `&sort=firstName,${sortOrder}&sort=middleName,${sortOrder}&sort=lastName,${sortOrder}&sort=lastName2,${sortOrder}`
            : `&sort=${sortField},${sortOrder}`
        }&is_active=${filter.isActive}`;

    filter.related?.forEach((id) => (fromString += `&related_id=${id}`));

    if (filter.searchTerm) {
      fromString += `&search_term=${encodeURIComponent(filter.searchTerm)}`;
    } else {
      if (filter.id) fromString += `&id_has=${encodeURIComponent(filter.id)}`;
      if (filter.username) fromString += `&username_has=${encodeURIComponent(filter.username)}`;
      if (filter.nameEmail) {
        const value = encodeURIComponent(filter.nameEmail);

        fromString += /^[^\s@]*@[^\s@]*$/.test(filter.nameEmail)
          ? `&email_has=${value}`
          : `&name_has=${value}`;
      }
      if (filter.cellPhone) fromString += `&cell_phone_has=${encodeURIComponent(filter.cellPhone)}`;

      filter.entityTypes?.forEach((type) => (fromString += `&entity_type=${encodeURIComponent(type)}`));
    }

    return fromString;
  }

  // CREATE =>  POST: add a new contact to the server
  createContact(contact: ContactModel): Observable<ContactModel> {
    const url = environment.CORE_BASE + '/companies/' + this.companyId + '/contacts';
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const body = cloneDeep(contact);
    delete body.id;
    delete body.location_id;
    return this.http.post<ContactModel>(url, body, { headers: httpHeaders });
  }

  getHistoryByVersion(contactId: number, version?: number) {
    const contactUrl = `${environment.CORE_BASE}/companies/${this.companyId}/contacts/${contactId}`;
    const contactHistoryUrl =
      version > 0
        ? `${contactUrl}/history?versions=${version - 1}&versions=${version}`
        : `${contactUrl}/history?versions=${version}`;
    const contactGetHistoryUrl =
      version == null ? `${contactUrl}/history?versions=` : contactHistoryUrl;
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);

    return this.http.get<any>(contactGetHistoryUrl, { headers });
  }

  findContacts(queryParams: QueryParamsModel): Observable<QueryResultsModel> {
    const params = new HttpParams({
      fromString: ContactsService.getContactsListQueryString(queryParams),
    });

    return this.http.get<QueryResultsModel>(
      `${environment.CORE_BASE}/companies/${this.companyId}/contacts${
        queryParams.filter.isInbound ? '/inbound_call' : ''
      }`,
      { params },
    );
  }
  findContactsWithDublicateCellPhone(cell_phone: string): Observable<QueryResultsModel> {
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);

    const params = new HttpParams().set('cell_phone', cell_phone);

    return this.http.get<QueryResultsModel>(
      `${environment.CORE_BASE}/companies/${this.companyId}/contacts/check_cell_phone`,
      { headers, params },
    );
  }

  getContactsAsCSV(queryParams: QueryParamsModel): Observable<any> {
    const params = new HttpParams({
      fromString: ContactsService.getContactsListQueryString(queryParams),
    });

    return this.http.get(`${environment.CORE_BASE}/companies/${this.companyId}/contacts/csv`, {
      params,
      responseType: 'text',
    });
  }

  getContactsByIds(contactIds: any[]) {
    if (contactIds.length === 0) {
      return of({});
    }
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    let ids = '';
    // tslint:disable-next-line:no-shadowed-variable
    contactIds = uniqWith(contactIds, isEqual);
    contactIds.forEach((element) => (ids = ids + '&id=' + element));
    const params = new HttpParams({ fromString: ids });
    return this.http.get<any>(
      environment.CORE_BASE +
        '/companies/' +
        this.companyId +
        '/contacts?page=0&size=' +
        contactIds.length,
      {
        headers,
        params,
      },
    );
  }

  getContactByEntityId(id) {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const url =
      environment.CORE_BASE + '/companies/' + this.companyId + '/contacts?entity_id=' + id;
    return this.http.get<any>(url, { headers: httpHeaders });
  }

  getContactByRelatedId(id, search_term) {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const related_id = `&related_id=${id}`;
    const url =
      environment.CORE_BASE +
      '/companies/' +
      this.companyId +
      '/contacts?' +
      '&search_term=' +
      search_term +
      related_id;
    return this.http.get<any>(url, { headers: httpHeaders });
  }
  getContactByRelatedIdAndApps(ids, apps, search_term) {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    let related_id = '';
    ids.forEach((id) => (id ? (related_id += `&related_id=${id}`) : ''));
    let appsIDs = '';
    apps = uniqWith(apps, isEqual);
    apps.forEach((element) => (appsIDs = appsIDs + '&app_id=' + element));
    const url =
      environment.CORE_BASE +
      '/companies/' +
      this.companyId +
      '/contacts?' +
      '&search_term=' +
      search_term +
      related_id +
      appsIDs;
    return this.http.get<any>(url, { headers: httpHeaders });
  }
  searchContacts(search_term: string): Observable<any> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const url =
      environment.CORE_BASE +
      '/companies/' +
      this.companyId +
      '/contacts?page=0&size=40&sort=firstName,asc&search_term=' +
      search_term;
    return this.http.get<any>(url, { headers: httpHeaders });
  }

  searchContactByField(term = '', field?: string): any {
    return this.http.get<any>(
      `${environment.CORE_BASE}/companies/${
        this.companyId
      }/contacts?page=0&size=40&sort=firstName,asc${
        field ? `&${field}=${term}` : `&search_term=${term}`
      }`,
    );
  }
  searchContactByFieldAndApp(apps, term = ''): any {
    let appsIDs = '';
    apps = uniqWith(apps, isEqual);
    apps.forEach((element) => (appsIDs = appsIDs + '&app_id=' + element));
    return this.http.get<any>(
      `${environment.CORE_BASE}/companies/${this.companyId}/contacts?page=0&size=40&sort=firstName,asc&search_term=${term}${appsIDs}`,
    );
  }
  searchContactRelationByField(entityId?: any, fromAffi?: any, term = '', field?: string): any {
    if (fromAffi) {
      return this.http.get<any>(
        `${environment.CORE_BASE}/companies/${
          this.companyId
        }/contacts?page=0&size=40&sort=firstName,asc${
          field ? `&${field}=${term}` : `&search_term=${term}`
        }${entityId ? `&not_related_to_affiliation=${entityId}` : ''}`,
      );
    } else
      return this.http.get<any>(
        `${environment.CORE_BASE}/companies/${
          this.companyId
        }/contacts?page=0&size=40&sort=firstName,asc${
          field ? `&${field}=${term}` : `&search_term=${term}`
        }${entityId ? `&not_related_to_entity=${entityId}` : ''}`,
      );
  }
  searchContactsFollowups(relatedIds, search_term: string): Observable<any> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    let param = '';
    relatedIds = uniqWith(relatedIds, isEqual);
    relatedIds.forEach((element) => (param = param + '&related_id=' + element));
    const url =
      environment.CORE_BASE +
      '/companies/' +
      this.companyId +
      '/contacts?sort=firstName,asc&search_term=' +
      search_term +
      param;
    return this.http.get<any>(url, { headers: httpHeaders });
  }

  // UPDATE => PUT: update the contact on the server
  updateContact(contact: ContactModel): Observable<any> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const url = environment.CORE_BASE + '/companies/' + this.companyId + '/contacts/' + contact.id;
    return this.http.put(url, contact, { headers: httpHeaders });
  }

  // UPDATE Status
  updateStatusForContact(id: number, is_active: boolean): Observable<any> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const body = {
      id,
      is_active,
    };
    const url = environment.CORE_BASE + '/contactstatusupdatesingle';
    return this.http.put(url, body, { headers: httpHeaders });
  }

  // UPDATE Status
  updateStatusForContacts(ids: number[], is_active: boolean): Observable<any> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const body = {
      ids,
      is_active,
    };
    const url = environment.CORE_BASE + '/contactstatusupdate';
    return this.http.put(url, body, { headers: httpHeaders });
  }

  // DELETE => delete the contact from the server
  deleteContact(contactId: number): Observable<any> {
    const url = environment.CORE_BASE + '/companies/' + this.companyId + '/contacts/' + contactId;
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.http.delete<any>(url, { headers: httpHeaders });
  }

  deleteContacts(contactsIds: number[] = []): Observable<any> {
    const url = environment.CORE_BASE + '/multiple-soft-delete-contact';
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const body = {
      ids: contactsIds,
    };
    return this.http.post<any>(url, body, { headers: httpHeaders });
  }

  searchRelatedPerson(search_term: string, entity_id): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set('Content-Type', 'application/json');
    headers = headers.set('Authorization', 'Bearer ' + this.authToken);
    const url =
      environment.CORE_BASE +
      '/companies/' +
      this.companyId +
      '/entities/' +
      entity_id +
      '/relations?term=' +
      search_term;
    return this.http.get<any>(url, { headers });
  }

  putContactMerge(contacts, contactId) {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const body = {
      other_contacts: contacts,
    };
    const url =
      environment.CORE_BASE + '/companies/' + this.companyId + '/contacts/' + contactId + '/merge';
    return this.http.put(url, body, { headers: httpHeaders, observe: 'response' });
  }

  updateApplications(item, contactId) {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const url = environment.MOBILE_BASE + `/api/v1/user/${contactId}/applications`;
    return this.http.put(
      url,
      { applications: item },
      { headers: httpHeaders, observe: 'response' },
    );
  }
  updateUsername(username, contactId) {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const url = environment.MOBILE_BASE + `/api/v1/user/${contactId}/username`;
    return this.http.put(url, username, { headers: httpHeaders, observe: 'response' });
  }
  getLanguagePreference() {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const url = environment.CORE_BASE + '/language_preference';
    return this.http.get<any>(url, { headers: httpHeaders });
  }

  getLoginHistory(id, queryParams?: QueryParamsModel) {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const page = queryParams ? queryParams.pageNumber : 0;
    const size = queryParams ? queryParams.pageSize : 10;
    const sortField = queryParams?.sortField
      ? queryParams.sortField.replace(/([-_]\w)/g, (g) => g[1].toUpperCase())
      : 'created';
    const sortOrder = queryParams?.sortOrder ? queryParams.sortOrder : 'desc';
    const url =
      environment.MOBILE_BASE +
      `/api/v1/user/${id}/login-history?page=${page}&size=${size}&sort=${sortField},${sortOrder}`;
    return this.http.get<any>(url, { headers: httpHeaders });
  }

  signupContact(body) {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const url = environment.MOBILE_BASE + `/api/v1/user/signup`;
    return this.http.post<any>(url, body, { headers: httpHeaders });
  }

  resetPassword(body) {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const url = environment.MOBILE_BASE + `/api/v1/user/set-password`;
    return this.http.post<any>(url, body, { headers: httpHeaders });
  }

  updateContactImage(contactFile: File, pid): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.set('Authorization', 'Bearer ' + this.authToken);
    const url = `${environment.CORE_BASE}/companies/${this.companyId}/contacts/${pid}/attachment`;
    return this.http.post(url, { avatar: contactFile.name }, { headers });
  }

  uploadContactImage(s3, contactFile: File): Observable<any> {
    const url = s3.data.url;
    return this.http.put(url, contactFile, {});
  }

  contactAvatarHistory(type, contact_id) {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const url =
      environment.CORE_BASE +
      `/companies/${this.companyId}/${type}/${contact_id}/attachment/history`;
    return this.http.get<any>(url, { headers: httpHeaders });
  }

  avatarPresigned(uuid) {
    if (!uuid) {
      return of();
    }
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const url =
      environment.CORE_BASE +
      `/companies/${this.companyId}/entities/attachment/get_presigned_url?uuid=${encodeURIComponent(
        uuid,
      )}`;
    return this.http.get<any>(url, { headers: httpHeaders });
  }

  sendInvitation(contact_id) {
    const url =
      environment.CORE_BASE + `/companies/${this.companyId}/contacts/${contact_id}/invitations`;
    return this.http.post(url, {});
  }
}
